import React from 'react';
import {Box, FormControlLabel, Radio, RadioGroup, Stack, Switch, TextField, Typography} from '@mui/material';
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import {YesNoQuestion} from "crqlar-react-common/dist/models/forms/form";
import {useTranslation} from "react-i18next";

interface YesNoInputProps {
    question: YesNoQuestion;
    onQuestionUpdate: (question: YesNoQuestion) => void;
    isSelected?: boolean;
    onSelect?: () => void;
}

const YesNoInput: React.FC<YesNoInputProps> = ({
                                                   question,
                                                   onQuestionUpdate,
                                                   isSelected = false,
                                                   onSelect
                                               }) => {
    const {t} = useTranslation();

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            title: e.target.value
        });
    };

    const handleDescriptionChange = (value?: string) => {
        onQuestionUpdate({
            ...question,
            description: value || ''
        });
    };

    const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            required: e.target.checked
        });
    };

    return (
        <Stack
            onClick={onSelect}
            sx={{
                p: 3,
                borderRadius: 1,
                border: isSelected ? '2px solid' : '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: 'primary.light',
                    backgroundColor: 'action.hover'
                },
                position: 'relative'
            }}
        >
            <Box sx={{alignSelf: 'flex-end'}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.required || false}
                            onChange={handleRequiredChange}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                        />
                    }
                    label={<Typography variant="caption">{t('Required')}</Typography>}
                    labelPlacement="start"
                    onClick={(e) => e.stopPropagation()}
                    sx={{m: 0}}
                />
            </Box>

            <TextField
                fullWidth
                label={t('Question Label')}
                value={question.title}
                onChange={handleTitleChange}
                variant="standard"
                sx={{mb: 2, pr: 12}}
                onClick={(e) => e.stopPropagation()}
            />

            {isSelected ? (
                <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
                    <MdEditor
                        value={question.description || ''}
                        onChange={handleDescriptionChange}
                        placeholder={t('Question Description (optional)')}
                        className={'mini-mdx'}
                    />
                </Box>
            ) : (
                question.description && (
                    <Box sx={{mb: 2}}>
                        <MdRenderer>
                            {question.description}
                        </MdRenderer>
                    </Box>
                )
            )}

            <RadioGroup value="">
                <FormControlLabel
                    value="yes"
                    control={<Radio disabled/>}
                    label={t('Yes')}
                />
                <FormControlLabel
                    value="no"
                    control={<Radio disabled/>}
                    label={t('No')}
                />
            </RadioGroup>
        </Stack>
    );
};

export default YesNoInput;
